import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useMembers } from "@/context/MemberContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pill, Check, X, Sun, Sunset, Moon, Sunrise } from "lucide-react";
import { toast } from "sonner";

const SLOT_META = {
  morning:   { label: "Morning",   icon: Sunrise, hour: 8,  color: "#D8A24A" },
  afternoon: { label: "Afternoon", icon: Sun,     hour: 13, color: "#C76B58" },
  evening:   { label: "Evening",   icon: Sunset,  hour: 18, color: "#A85546" },
  night:     { label: "Night",     icon: Moon,    hour: 21, color: "#5B7582" },
};

function todayIso() { return new Date().toISOString().slice(0, 10); }

export default function MedAdherenceWidget() {
  const { activeId } = useMembers();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [date] = useState(todayIso());

  const load = useCallback(async () => {
    if (!activeId) return;
    setLoading(true);
    try {
      const r = await api.get(`/medications/adherence?date=${date}&member_id=${activeId}`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  }, [activeId, date]);

  useEffect(() => { load(); }, [load]);

  const log = async (med_id, slot, taken) => {
    try {
      await api.post("/medications/adherence", { med_id, date, slot, taken });
      // Optimistic update
      setItems(prev => prev.map(it => it.med_id !== med_id ? it : ({
        ...it,
        slots: it.slots.map(s => s.slot === slot ? { ...s, taken } : s),
      })));
    } catch { toast.error("Could not save"); load(); }
  };

  const { totalSlots, takenSlots, pct } = useMemo(() => {
    const total = items.reduce((a, it) => a + it.slots.length, 0);
    const taken = items.reduce((a, it) => a + it.slots.filter(s => s.taken === true).length, 0);
    return { totalSlots: total, takenSlots: taken, pct: total ? Math.round(100 * taken / total) : 0 };
  }, [items]);

  if (loading) {
    return (
      <Card className="p-5 rounded-2xl border-[#E5E7E4]" data-testid="adherence-widget">
        <p className="text-sm text-[#7D8F84]">Loading medications…</p>
      </Card>
    );
  }

  if (!items.length) return null;

  return (
    <Card className="p-5 sm:p-6 rounded-2xl border border-[#E5E7E4]" data-testid="adherence-widget">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-[#4A6750]/15 flex items-center justify-center">
          <Pill className="w-5 h-5 text-[#4A6750]" strokeWidth={1.5} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-semibold tracking-[0.05em] uppercase text-[#7D8F84]">Medications today</p>
          <p className="font-heading text-lg font-semibold leading-tight">{takenSlots} of {totalSlots} taken</p>
        </div>
        <RingPct pct={pct} />
      </div>

      <ul className="space-y-2.5">
        {items.map(it => (
          <li key={it.med_id} className="p-3 rounded-xl bg-[#F9F8F6] border border-[#E5E7E4]" data-testid={`med-row-${it.med_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-[#1C2E24] truncate">{it.name}</p>
                {(it.dosage || it.food_timing) && (
                  <p className="text-xs text-[#5C6D63] truncate">{[it.dosage, it.food_timing].filter(Boolean).join(" · ")}</p>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2.5">
              {it.slots.map(s => <SlotPill key={s.slot} slot={s.slot} taken={s.taken}
                                            onChange={(v) => log(it.med_id, s.slot, v)} />)}
            </div>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function SlotPill({ slot, taken, onChange }) {
  const meta = SLOT_META[slot];
  const Icon = meta?.icon || Sun;
  const color = meta?.color || "#7D8F84";
  const label = meta?.label || slot;

  const baseCls = "inline-flex items-center gap-1.5 rounded-full pl-2 pr-1 py-1 border text-xs transition-all";
  const stateCls =
    taken === true  ? "border-[#5B8266] bg-[#5B8266]/15 text-[#3B5340]" :
    taken === false ? "border-[#C76B58] bg-[#C76B58]/15 text-[#A85546]" :
    "border-[#E5E7E4] bg-white text-[#5C6D63]";

  return (
    <div className={`${baseCls} ${stateCls}`} data-testid={`slot-${slot}`}>
      <Icon className="w-3.5 h-3.5" style={{ color: taken == null ? color : undefined }} />
      <span className="font-medium">{label}</span>
      <div className="flex ml-1 gap-0.5">
        <button onClick={() => onChange(taken === true ? null : true)}
                title="Taken" data-testid={`slot-${slot}-take`}
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${taken === true ? "bg-[#5B8266] text-white" : "hover:bg-[#5B8266]/20 text-[#5B8266]"}`}>
          <Check className="w-3 h-3" strokeWidth={3} />
        </button>
        <button onClick={() => onChange(taken === false ? null : false)}
                title="Skip" data-testid={`slot-${slot}-skip`}
                className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${taken === false ? "bg-[#C76B58] text-white" : "hover:bg-[#C76B58]/20 text-[#C76B58]"}`}>
          <X className="w-3 h-3" strokeWidth={3} />
        </button>
      </div>
    </div>
  );
}

function RingPct({ pct }) {
  const r = 18, c = 2 * Math.PI * r;
  const dash = (pct / 100) * c;
  return (
    <div className="relative w-12 h-12">
      <svg className="w-12 h-12 -rotate-90" viewBox="0 0 44 44">
        <circle cx="22" cy="22" r={r} stroke="#E5E7E4" strokeWidth="4" fill="none" />
        <circle cx="22" cy="22" r={r} stroke="#4A6750" strokeWidth="4" fill="none"
                strokeLinecap="round" strokeDasharray={`${dash} ${c}`} />
      </svg>
      <div className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-[#1C2E24]">{pct}%</div>
    </div>
  );
}
